import React, { useMemo } from 'react';
import { useData } from 'resift';
import _get from 'lodash/get';
import classNames from 'classnames';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import sampleDataLookup from 'components/Mappings/_helpers/sampleDataLookup';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import TableCell from '@material-ui/core/TableCell';
import Collapse from '@material-ui/core/Collapse';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import SourceSelector from 'components/Mappings/SourceSelector';
import CollectionMapping, { MAPPING_PRESETS } from 'components/Mappings/CollectionMapping';
import {
  ORG_TEAM_LEADER_MAPPING_REF_KEY,
  DEFAULT_EMPTY_ORG_MAPPINGS,
  ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
} from 'pages/Directory/consts';
import MappingProps, * as MappingTypes from 'types/Mapping';
import Source, { SourceType } from 'types/Source';

interface Props {
  mappingIndex: number;
  mapping?: MappingProps;
  disabled?: boolean;
  existingTlMatchMapping?: MappingProps; //(note pearl) Optional prop for CSV auto tlRef detect case
  source: Source;
  destination: 'tlRef' | 'tlMatch';
  onUpdateMapping: (newMappingIndex: number, newMapping: MappingProps | MappingProps[]) => void;
  options: any[];
}

function OrgSourceMapping({
  mappingIndex,
  source,
  destination,
  mapping = {} as MappingProps,
  existingTlMatchMapping,
  onUpdateMapping,
  disabled,
  options,
}: Props) {
  const classes = useStyles();
  const value = _get(mapping, 'source', '');
  const rootName = value.split('.')[0];
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);
  const mappingAttributes = useData(getMappingsAttributes);
  const sampleUserData = _get(mappingAttributes, 'sampleData', {});
  const tlRefOptionsForCsv: MappingTypes.TlRefOption[] = _get(
    mappingAttributes,
    'tlRefOptions',
    [],
  );
  const isGoogle = source.type === SourceType.GOOGLE;
  const isCollapsed = useMemo(() => {
    return !!MAPPING_PRESETS.find(MP => MP.key === rootName && isGoogle);
  }, [rootName]);
  const tlName = isCollapsed ? rootName : value;
  const _sampleData = isCollapsed
    ? sampleDataLookup(sampleUserData, mapping)
    : _get(sampleUserData, mapping.source);
  const sampleData = _sampleData || '(none)';
  const noSampleData = sampleData === '(none)';
  const isCsvSource = source.type === SourceType.CSV;
  const isTlRefRow = destination === ORG_TEAM_LEADER_MAPPING_REF_KEY;
  const isTlMatchRow = destination === ORG_TEAM_LEADER_MAPPING_MATCH_KEY;

  const handleUpdateSource = (sourceValue: string) => {
    const newMapping: MappingProps = {
      ...mapping,
      source: sourceValue,
      type: 'logistical',
      collectionItemType: null,
      collectionItemCustomType: null,
      destination,
      customProps: {
        ...mapping.customProps,
      },
    };

    // (note - pearl) When this row is for tlMatch or when there is no tlRefOptions (undefined)
    if (isTlMatchRow || !tlRefOptionsForCsv || !isCsvSource) {
      onUpdateMapping(mappingIndex, newMapping);
    }

    // (note - pearl) When this row is tlRef row and there are tlRef options available
    // we would update both tlRef and tlMatch on a tlRef select:
    // 1) when there is only one corresponding tlMatch, automatically select that
    // 2) when there are more than one corresponding tlMatch, reset tlMatch to default tlMatch,
    //    with the prev tlMatch mapping id if there was one - setting the mapping id to prevent
    //    the triggering of 'create' request, instead would trigger an 'update' request
    if (isTlRefRow && isCsvSource) {
      const selectedTlRefOption = tlRefOptionsForCsv.find(item => item.sourcePath === sourceValue);
      const hasSingleTlMatchOption = _get(selectedTlRefOption, ['tlMatchOptions', 'length']) === 1;
      const _defaultTlMatchMapping = DEFAULT_EMPTY_ORG_MAPPINGS[0];
      const existingTlMatchMappingId = _get(existingTlMatchMapping, 'id');

      if (hasSingleTlMatchOption) {
        const singleTlMatchSourceValue = _get(selectedTlRefOption, [
          'tlMatchOptions',
          '0',
          'sourcePath',
        ]) as string;

        const newTlMatchMapping: MappingProps = {
          ..._defaultTlMatchMapping,
          source: singleTlMatchSourceValue,
          type: 'logistical',
          id: existingTlMatchMappingId,
          collectionItemType: null,
          collectionItemCustomType: null,
          destination: ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
        };

        onUpdateMapping(0, [newTlMatchMapping, newMapping]);
      } else {
        const defaultTlMatchMapping = {
          ..._defaultTlMatchMapping,
          id: existingTlMatchMappingId,
        };
        onUpdateMapping(mappingIndex, [defaultTlMatchMapping, newMapping]);
      }
    }
  };

  const handleCollectionChange = (newMapping: MappingProps) => {
    onUpdateMapping(mappingIndex, {
      ...newMapping,
      destination,
    });
  };

  const handleClearMapping = () => {
    const defaultMapping = isTlRefRow
      ? DEFAULT_EMPTY_ORG_MAPPINGS[1]
      : DEFAULT_EMPTY_ORG_MAPPINGS[0];

    onUpdateMapping(mappingIndex, {
      ...defaultMapping,
      destination,
    });

    // (note - pearl) When it's a tlRef row and that we automatically detect tlMatch (when
    // tlRefOptions is defined, including []), clearing the tlRef would also need to clear tlMatch
    if (isTlRefRow && !!tlRefOptionsForCsv && isCsvSource) {
      onUpdateMapping(mappingIndex, DEFAULT_EMPTY_ORG_MAPPINGS);
    }
  };

  return (
    <>
      <TableRow className={classNames({ [classes.disabledRow]: disabled })}>
        <TableCell className={disabled ? classes.disabledRow : undefined}>
          {isTlRefRow && (
            <Typography variant="caption" color="textSecondary">
              Direct Manager {isCsvSource ? 'Header' : 'Property'}
            </Typography>
          )}
          {isTlMatchRow && (
            <Typography variant="caption" color="textSecondary">
              Employee {isCsvSource ? 'Header' : 'Property'}
            </Typography>
          )}
          <div className={classNames({ [classes.disabledSelector]: disabled })}>
            <SourceSelector
              onChange={handleUpdateSource}
              value={value}
              isSourceCollapsed={isCollapsed}
              isCsvSource={isCsvSource}
              sourceAttrOptions={options}
              isDisabled={disabled}
            />
          </div>
        </TableCell>
        <TableCell
          title={sampleData}
          className={classNames(classes.ellipsisText, noSampleData && classes.noSampleData)}
        >
          {sampleData}
        </TableCell>
        <TableCell>{value && <Button onClick={handleClearMapping}>Clear</Button>}</TableCell>
        <TableCell />
      </TableRow>
      {isCollapsed && (
        <TableRow className={classes.collectionMappingRow}>
          <TableCell colSpan={3}>
            <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
              <CollectionMapping
                mapping={mapping}
                value={tlName}
                onChange={handleCollectionChange}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  ellipsisText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingTop: theme.spacing(4),
  },
  noSampleData: {
    color: theme.palette.text.disabled,
  },
  disabledRow: {
    color: theme.palette.text.disabled,
  },
  disabledSelector: {
    pointerEvents: 'none',
  },
  collectionMappingRow: {
    backgroundColor: theme.palette.grey[100],
  },
  warning: {
    color: theme.palette.warning.main,
    width: 'fit-content',
    marginLeft: 'auto',
    paddingTop: theme.spacing(4),
  },
}));

export default OrgSourceMapping;
